import { ReactNode } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { NavLink } from 'libs/router'
import { useTranslation } from 'libs/i18n'
import { format, parseISO } from 'libs/dates'
import { useUserRoles } from 'shared/hooks'
import { ContextMenu, Currency } from 'shared/components'
import { BooleanBadge } from 'shared/atoms'
import type { CardTableColumn } from 'styles/table'
import type { Option } from 'shared/components/ContextMenu/types'
import { ActivityStatus, BookingStatus } from 'features/bookings'
import type { BookingItem } from '../types/bookings.types'
import { PaymentStatus } from '../molecules/PaymentStatus/PaymentStatus'
import { useBookingPath } from './useContextMenuOptions'

type UseColumnsProps = {
	getMenuOptions: (booking: BookingItem) => Option[]
}

const cell = (render: (booking: BookingItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as BookingItem)

const numeric = 'block whitespace-nowrap text-right tabular-nums'
const dash = <span className="text-muted-foreground">—</span>
const date = (value: string) => format(new Date(value), 'dd.MM.yyyy')

export const useColumns = ({ getMenuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const bookingPath = useBookingPath()
	const quantity = new Intl.NumberFormat(navigator.language || 'is')

	return [
		{
			label: t('Booking'),
			width: '90px',
			renderCell: cell((item) => (
				<NavLink
					className="text-[13px] font-bold tabular-nums text-primary hover:underline"
					to={bookingPath(item.id)}>
					{item.ref_nr}
				</NavLink>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Created'),
			width: '100px',
			renderCell: cell((item) => (
				<span className="block whitespace-nowrap tabular-nums">
					<span className="block">{format(parseISO(String(item.created)), 'dd.MM.yyyy')}</span>
					<span className="block text-[11px] text-muted-foreground">
						{format(parseISO(String(item.created)), 'HH:mm:ss')}
					</span>
				</span>
			))
		},
		...(isStaff
			? [
					{
						label: t('Status'),
						width: '100px',
						renderCell: cell((item) => (
							<ActivityStatus className="tracking-wide" status={item.is_active} />
						))
					}
				]
			: []),
		{
			label: t('Booking status'),
			width: '130px',
			renderCell: cell((item) => <BookingStatus className="tracking-wide" status={item.status} />)
		},
		...(isStaff
			? [
					{
						label: t('User'),
						width: 'minmax(170px, 1fr)',
						renderCell: cell((item) => (
							<span className="block leading-snug">
								<span className="block truncate font-medium">{item.user?.name}</span>
								<span className="block truncate text-[11px] text-muted-foreground">
									{item.user?.email}
								</span>
							</span>
						))
					}
				]
			: []),
		{
			label: t('Type'),
			width: 'minmax(180px, 1fr)',
			renderCell: cell((item) => (
				<span className="block truncate">{item.booth?.booth_type?.name || dash}</span>
			))
		},
		{
			label: t('Area'),
			width: '70px',
			renderCell: cell((item) =>
				item.booth?.number ? <span className="tabular-nums">#{item.booth.number}</span> : dash
			)
		},
		{
			label: t('Days'),
			width: '70px',
			align: 'right',
			renderCell: cell((item) => (
				<span className={numeric}>
					{typeof item.rental !== 'number' && item.rental?.duration != null
						? item.rental.duration
						: dash}
				</span>
			))
		},
		{
			label: t('From'),
			width: '100px',
			renderCell: cell((item) => <span className="tabular-nums">{date(item.start_date)}</span>)
		},
		{
			label: t('To'),
			width: '100px',
			renderCell: cell((item) => <span className="tabular-nums">{date(item.end_date)}</span>)
		},
		{
			label: t('Payment'),
			width: '120px',
			renderCell: cell((item) => <PaymentStatus status={item.payment_status} />)
		},
		{
			label: t('Payment method'),
			width: '130px',
			renderCell: cell((item) => item.payment_method || dash)
		},
		{
			label: t('Products sold'),
			width: '120px',
			align: 'right',
			renderCell: cell((item) => (
				<span className={numeric}>{quantity.format(+item.sold_quantity)}</span>
			))
		},
		{
			label: t('Amount sold'),
			width: '120px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency
					className={numeric}
					price={{ amount: +item.sold_total, currency: item.total_value.currency }}
				/>
			))
		},
		{
			label: t('Products remaining'),
			width: '150px',
			align: 'right',
			renderCell: cell((item) => (
				<span className={numeric}>{quantity.format(item.remaining_products)}</span>
			))
		},
		{
			label: t('Amount remaining'),
			width: '140px',
			align: 'right',
			renderCell: cell((item) => (
				<Currency className={`${numeric} font-semibold`} price={item.total_value} />
			))
		},
		...(isStaff
			? [
					{
						label: t('Takedown booked'),
						width: '140px',
						align: 'center' as const,
						renderCell: cell((item) => (
							<span className="block text-center">
								<BooleanBadge
									size="sm"
									value={!!item.lifecycle?.customer_notified_takedown_at}
									label={t('Takedown booked')}
								/>
							</span>
						))
					},
					{
						label: t('Notes'),
						width: '80px',
						align: 'center' as const,
						renderCell: cell((item) => (
							<span className="block text-center">
								<BooleanBadge size="sm" value={!!item.store_notes} label={t('Notes')} />
							</span>
						))
					}
				]
			: []),
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={getMenuOptions(item)}
					label={t('Actions for booking {{ id }}', { id: item.ref_nr })}
				/>
			))
		}
	]
}
